import Layout from "@/components/feature/Layout";
import { useMemo } from "react";
import { alacaklarData } from "@/mocks/alacaklar";
import { siparislerData } from "@/mocks/siparisler";
import { borclarData } from "@/mocks/borclar";

function formatCurrency(value: number, currency = "₺"): string {
  return `${value.toLocaleString("tr-TR")} ${currency}`;
}

function MiniBar({ value, max, color = "gold" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const barColor =
    color === "gold"
      ? "bg-gold-500"
      : color === "red"
      ? "bg-red-500"
      : color === "emerald"
      ? "bg-emerald-500"
      : color === "sky"
      ? "bg-sky-500"
      : "bg-gray-500";
  return (
    <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
      <div className={`${barColor} h-full rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function Raporlar() {
  const aylikTahsilat = useMemo(() => {
    const months = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran"];
    const data = [45000, 62000, 38000, 89000, 124500, 78000];
    const max = Math.max(...data);
    return months.map((m, i) => ({ month: m, value: data[i], max }));
  }, []);

  const durumDagilimi = useMemo(() => {
    const counts: Record<string, number> = {};
    siparislerData.forEach((s) => {
      counts[s.durum] = (counts[s.durum] || 0) + 1;
    });
    const total = siparislerData.length;
    return Object.entries(counts).map(([durum, count]) => ({
      durum,
      count,
      pct: total > 0 ? Math.round((count / total) * 100) : 0,
    }));
  }, []);

  const totalAlacak = alacaklarData.reduce((s, a) => s + a.kalanTutar, 0);
  const totalBorc = borclarData.reduce((s, b) => s + b.kalan, 0);
  const gecikenSayi = alacaklarData.filter((a) => a.durum === "Gecikti").length;
  const gecikenTutar = alacaklarData.filter((a) => a.durum === "Gecikti").reduce((s, a) => s + a.kalanTutar, 0);

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-100 mb-1">Raporlar</h1>
        <p className="text-sm text-gray-500">İşletmenizin finansal ve operasyonel raporları.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aylık Tahsilat */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <i className="ri-bar-chart-grouped-line text-gold-400" />
            Aylık Tahsilat Grafiği
          </h3>
          <div className="space-y-3">
            {aylikTahsilat.map((item) => (
              <div key={item.month}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-gray-400">{item.month}</span>
                  <span className="text-gray-200 font-medium">{formatCurrency(item.value)}</span>
                </div>
                <MiniBar value={item.value} max={item.max} color="gold" />
              </div>
            ))}
          </div>
        </div>

        {/* Geciken Ödemeler */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-400" />
            Geciken Ödemeler Özeti
          </h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-red-500/10 rounded-lg p-3">
              <p className="text-xs text-red-400 mb-1">Geciken Müşteri</p>
              <p className="text-xl font-semibold text-red-400">{gecikenSayi}</p>
            </div>
            <div className="bg-red-500/10 rounded-lg p-3">
              <p className="text-xs text-red-400 mb-1">Geciken Tutar</p>
              <p className="text-xl font-semibold text-red-400">{formatCurrency(gecikenTutar)}</p>
            </div>
          </div>
          <div className="space-y-2">
            {alacaklarData
              .filter((a) => a.durum === "Gecikti")
              .map((item) => (
                <div key={item.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-300 font-medium">{item.adSoyad}</p>
                    <p className="text-[10px] text-gray-500">{item.odemeTarihi}</p>
                  </div>
                  <p className="text-sm text-red-400 font-medium">{formatCurrency(item.kalanTutar)}</p>
                </div>
              ))}
          </div>
        </div>

        {/* Sipariş Durum Dağılımı */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <i className="ri-pie-chart-line text-sky-400" />
            Sipariş Durum Dağılımı
          </h3>
          <div className="space-y-3">
            {durumDagilimi.map((item) => (
              <div key={item.durum}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-gray-400">{item.durum}</span>
                  <span className="text-gray-200 font-medium">
                    {item.count} adet ({item.pct}%)
                  </span>
                </div>
                <MiniBar
                  value={item.count}
                  max={siparislerData.length}
                  color={
                    item.durum === "Teslime Hazır"
                      ? "emerald"
                      : item.durum === "Atölyede"
                      ? "sky"
                      : item.durum === "Hazırlanıyor"
                      ? "gold"
                      : item.durum === "İptal"
                      ? "red"
                      : "gray"
                  }
                />
              </div>
            ))}
          </div>
        </div>

        {/* Finansal Özet */}
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <i className="ri-funds-line text-gold-400" />
            Finansal Özet
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gold-500" />
                <span className="text-xs text-gray-300">Toplam Alacak</span>
              </div>
              <span className="text-sm font-medium text-gold-400">{formatCurrency(totalAlacak)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-xs text-gray-300">Toplam Borç</span>
              </div>
              <span className="text-sm font-medium text-red-400">{formatCurrency(totalBorc)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs text-gray-300">Net Kasa</span>
              </div>
              <span className="text-sm font-medium text-emerald-400">{formatCurrency(totalAlacak - totalBorc)}</span>
            </div>
            <MiniBar value={totalAlacak - totalBorc} max={totalAlacak} color="emerald" />
          </div>
        </div>
      </div>
    </Layout>
  );
}
